import { FilterInputType, FilterInputTemplate } from "@/app/home/components/filter/FilterBar";
import { ReportTableConfigs } from "@/app/home/components/report/ReportTable";
import { GlobalUtils } from "@/app/home/utils/global.utils";
import { BillingPaymentType, PaymentMethodType, WaterInvoiceStatus } from "@/core/enums/global.enums";
import { WaterInvoiceModel } from "@/core/models/water-invoice.model";
import { approveStatusPipe } from "@/core/pipes/approve-status.pipe";
import { billingPaymentTypePipe } from "@/core/pipes/billing-payment-type.pipe";
import { formatCurrencyPipe } from "@/core/pipes/format-currency.pipe";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";
import { invoiceStatusPipe, invoiceStatusTagsPipe } from "@/core/pipes/invoice-status.pipe";
import { paymentMethodPipe } from "@/core/pipes/payment-method-pipe";
import { cubicMeterPipe } from "@/core/pipes/unit.pipe";
import { AddressRepo } from "@/core/repos/address.repo";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import { UnitTypeRepo } from "@/core/repos/tables/unit-type.repo";
import { WaterInvoiceRepo } from "@/core/repos/tables/water-invoice.repo";
import { UserRepo } from "@/core/repos/user.repo";
import container from "@/lib/di/container";
import dayjs from "dayjs";

const tollAreaRepo = container.get(TollAreaRepo);
const waterInvoiceRepo = container.get(WaterInvoiceRepo);
const addressRepo = container.get(AddressRepo);
const unitTypeRepo = container.get(UnitTypeRepo);
const userRepo = container.get(UserRepo);

export const INVOICE_REPORT_CONFIGS: {name: string, props: ReportTableConfigs<WaterInvoiceModel, null>} = {
    name: "Báo cáo Hoá đơn Bảng thu phí",
    props: {
        initialFilterMap: new Map(
            Object.entries({...GlobalUtils.getPreviousMonthFilter()})
        ),
        columns: [
            { name: "Tháng", pipe: (value: WaterInvoiceModel) => value.month + "/" + value.year, fixed: "left" },
            { name: "Số TC", dataIndex: "code" },
            { name: "HDĐT", dataIndex: "eInvoiceNo" },
            { name: "Mã HĐ", pipe: (value: WaterInvoiceModel) => value.waterUser?.code ?? "" },
            { name: "Hình thức thanh toán", pipe: (value: WaterInvoiceModel) => paymentMethodPipe(value.waterUser?.paymentMethod ?? "") },
            { name: "Tên KH", pipe: (value: WaterInvoiceModel) => value.waterUser?.name ?? "" },
            { name: "SĐT", pipe: (value: WaterInvoiceModel) => value.waterUser?.phone ?? "" },
            { name: "CS cuối", dataIndex: "waterMeterNumber", pipe: cubicMeterPipe },
            { name: "CS đầu", dataIndex: "waterMeterNumberLast", pipe: cubicMeterPipe },
            { name: "Khối lượng nước", dataIndex: "amount", pipe: cubicMeterPipe },
            { name: "Tiền thanh toán", dataIndex: "total", pipe: formatCurrencyPipe },
            { name: "Phê duyệt", dataIndex: "approveStatus", pipe: approveStatusPipe },
            { name: "Thời gian xuất HĐĐT", dataIndex: "eInvoiceArisingDate", pipe: (e) => formatDateTimePipe(e).length > 0 ? formatDateTimePipe(e) : "Chưa gửi" },
            { name: "Thời gian gửi", dataIndex: "sendDate", pipe: (e) => formatDateTimePipe(e).length > 0 ? formatDateTimePipe(e) : "Chưa gửi" },
            { name: "Thời gian TT", dataIndex: "paymentDate", pipe: (e) => formatDateTimePipe(e).length > 0 ? formatDateTimePipe(e) : "" },
            { name: "Người thanh toán", dataIndex: "payBillCreator" },
            { name: "TT thanh toán", dataIndex: "status", pipe: invoiceStatusTagsPipe, fixed: "right" },
        ],
        filterTemplates:[
            {
                dataIndex: "search",
                label: "Tìm kiếm",
                placeholder: "Nhập nội dung tìm kiếm",
                type: FilterInputType.text,
            },
            {
                dataIndex: "month",
                label: "Tháng",
                placeholder: "Chọn tháng",
                type: FilterInputType.month,
            },
            {
                dataIndex: "unitTypeId",
                label: "Loại hình",
                placeholder: "Chọn Loại hình",
                type: FilterInputType.select,
                options: []
            },
            {
                dataIndex: "provinceId",
                label: "Tỉnh/ TP",
                placeholder: "Chọn Tỉnh/ TP",
                type: FilterInputType.select,
                options: []
            },
            {
                dataIndex: "districtId",
                label: "Quận/ Huyện",
                placeholder: "Chọn Quận/ Huyện",
                type: FilterInputType.select,
                options: []
            },
            {
                dataIndex: "wardId",
                label: "Xã/ Phường",
                placeholder: "Chọn Xã/ Phường",
                type: FilterInputType.select,
                options: []
            },
            {
                dataIndex: "tollAreaId",
                label: "Khu vực",
                placeholder: "Chọn Khu vực",
                type: FilterInputType.select,
                options: []
            },
            {
                dataIndex: "paymentMethod",
                label: "Hình thức thanh toán",
                placeholder: "Chọn hình thức thanh toán",
                type: FilterInputType.select,
                options: [
                    {value: PaymentMethodType.All, name: paymentMethodPipe(PaymentMethodType.All)},
                    {value: PaymentMethodType.Bank, name: paymentMethodPipe(PaymentMethodType.Bank)},
                    {value: PaymentMethodType.CentralPaid, name: paymentMethodPipe(PaymentMethodType.CentralPaid)},
                    {value: PaymentMethodType.HomePay, name: paymentMethodPipe(PaymentMethodType.HomePay)},
                    {value: PaymentMethodType.EWallet, name: paymentMethodPipe(PaymentMethodType.EWallet)},
                    {value: PaymentMethodType.Manual, name: paymentMethodPipe(PaymentMethodType.Manual)},
                    {value: PaymentMethodType.Transfer, name: paymentMethodPipe(PaymentMethodType.Transfer)},
                ]
            },
            {
                dataIndex: "status",
                label: "Tình trạng thanh toán",
                placeholder: "Chọn tình trạng thanh toán",
                type: FilterInputType.select,
                options: Object.keys(WaterInvoiceStatus).map(key => ({ value: key, name: invoiceStatusPipe(key as WaterInvoiceStatus) }))
            },
            {
                dataIndex: "payBillCreator",
                label: "Người thanh toán",
                placeholder: "Chọn Người thanh toán",
                type: FilterInputType.select,
                options: [
                    {
                        name: "VNPayQr",
                        value: "VNPayQr"
                    },
                    {
                        name: "VietinBank",
                        value: "VietinBank"
                    }
                ]
            },
            {
                rangeIndex: {from: "InvoiceTimeFrom", to: "InvoiceTimeTo"},
                type: FilterInputType.dateRange,
                placeholder: "Xuất HĐ từ ngày",
                secondaryPlaceholder: "Đến ngày",
                label: "Thời điểm Xuất HĐĐT",
                span: 2,
            },
            {
                dataIndex: "paymentType",
                label: "Loại thanh toán",
                placeholder: "Chọn loại thanh toán",
                type: FilterInputType.select,
                options: [
                    { value: BillingPaymentType.Transfer, name: billingPaymentTypePipe(BillingPaymentType.Transfer) },
                    { value: BillingPaymentType.Cash, name: billingPaymentTypePipe(BillingPaymentType.Cash) },
                    { value: BillingPaymentType.Debtminusdeposit, name: billingPaymentTypePipe(BillingPaymentType.Debtminusdeposit) },
                    { value: BillingPaymentType.CollectVNPayQr, name: billingPaymentTypePipe(BillingPaymentType.CollectVNPayQr) },
                    { value: BillingPaymentType.CollectEmployee, name: billingPaymentTypePipe(BillingPaymentType.CollectEmployee) },
                ]
            },
        ],
        handleExport: async (filterMap) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const query: any = {};
            filterMap.forEach(filter => {
                if (filter.value) {
                    query[filter.key] = filter.value
                }
            })  
            const blobData = await waterInvoiceRepo.exportExcelAll(query);
            if (blobData) {
                GlobalUtils.downloadXLSX(blobData, `ThuPhi-TatCa-${dayjs().format("DDMMYYYY-hhmm")}`)
            };
        },
        init: async (filterTemplates) => {
            const tollAreas = (await tollAreaRepo.getAll()).map(r => ({ name: r.name, value: r.id }));
            const provinces = (await addressRepo.getAllProvinces()).map(data => ({ name: data[1].name, value: data[0] }));
            const unitTypes = (await unitTypeRepo.getAll()).map(r => ({ name: r.name, value: r.id }));
            const users = (await userRepo.getFromFactory()).map(r => ({name: `${r.fullName}-${r.userName.toUpperCase()}`, value: r.userName}))
            const templates: FilterInputTemplate[] = [...filterTemplates];
            const tollAreaTemplate = templates.find(template => template.dataIndex === "tollAreaId");
            if (tollAreaTemplate) {
                tollAreaTemplate.options = tollAreas;
            }
            const provinceTemplate = templates.find(template => template.dataIndex === "provinceId");
            if (provinceTemplate) {
                provinceTemplate.options = provinces;
            }
            const unitTypeTemplate = templates.find(template => template.dataIndex === "unitTypeId");
            if (unitTypeTemplate) {
                unitTypeTemplate.options = unitTypes;
            }
            const userTemplate = templates.find(template => template.dataIndex === "payBillCreator");
            if (userTemplate) {
                userTemplate.options = [
                    {
                        name: "VNPayQr",
                        value: "VNPayQr"
                    },
                    {
                        name: "VietinBank",
                        value: "VietinBank"
                    },
                    ...users
                ];
            }
            return {
                filterTemplates: templates
            }
        },
        onFilterChanged: async (map, changes, filterTemplates) => {
            let isDirty = false;
            const templates: FilterInputTemplate[] = [...filterTemplates];
    
            if (changes.find(e => e === "provinceId")) {
                isDirty = true;
                const districts = (await addressRepo.getAllDistricts(map.get("provinceId")?.value??"")).map(data => ({name: data[1].name, value: data[0]}));
                const districtTemplate = templates.find(template => template.dataIndex === "districtId");
                if (districtTemplate) {
                    map.delete("districtId");
                    districtTemplate.options = districts;
                }
                const wardTemplate = templates.find(template => template.dataIndex === "wardId");
                if (wardTemplate) {
                    map.delete("wardId");
                    wardTemplate.options = [];
                }
            }
    
            if (changes.find(e => e === "districtId")&&!changes.find(e => e === "provinceId")) {
                isDirty = true;
                const wards = (await addressRepo.getAllWards(map.get("districtId")?.value??"")).map(data => ({name: data[1].name, value: data[0]}));
                const wardTemplate = templates.find(template => template.dataIndex === "wardId");
                if (wardTemplate) {
                    map.delete("wardId");
                    wardTemplate.options = wards;
                }
            }
    
            if (isDirty) {
                return {
                    filterTemplates: templates
                }
            };
            return {
                filterTemplates: undefined
            }
        },
        repository: waterInvoiceRepo
    }
}